import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useMemo } from 'react';
import { useCartStore } from '@/store/cart';
import { BUNDLE_DEALS, type BundleDeal } from '@/lib/bundleDeals';
import { formatPrice } from '@/lib/shopify';

/**
 * In the cart drawer: for every bundle that has at least one matching
 * line item, show a small progress row — either "add N more" or
 * "bundle unlocked". The actual discount applies at Shopify checkout
 * via the configured automatic discounts.
 */
export default function CartBundleProgress({ onClose }: { onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || 'en';
  const items = useCartStore((s) => s.items);

  const rows = useMemo(() => {
    return BUNDLE_DEALS.map((deal) => {
      const matching = items
        .filter((i) => Math.abs(i.price - deal.unitPrice) < 0.01)
        .reduce((sum, i) => sum + i.quantity, 0);
      return { deal, matching };
    }).filter((r) => r.matching > 0);
  }, [items]);

  if (rows.length === 0) return null;

  return (
    <div className="shrink-0 px-5 md:px-6 pt-3 pb-2 border-t border-border/40 bg-pastel-soft/30">
      <p className="font-body text-[0.55rem] tracking-[0.4em] uppercase text-foreground/55 mb-2">
        {t('cart.bundles.title', 'Bundle progress')}
      </p>
      <ul
        role="list"
        className="flex flex-col gap-1.5 max-h-[10vh] md:max-h-[11vh] overflow-y-auto overscroll-contain pr-1"
        data-lenis-prevent
      >
        {rows.map(({ deal, matching }) => (
          <BundleProgressRow
            key={deal.id}
            deal={deal}
            inCart={matching}
            lang={lang}
            onClose={onClose}
          />
        ))}
      </ul>
    </div>
  );
}

function BundleProgressRow({
  deal,
  inCart,
  lang,
  onClose,
}: {
  deal: BundleDeal;
  inCart: number;
  lang: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const unlocked = inCart >= deal.qty;
  const remaining = Math.max(0, deal.qty - inCart);
  const pct = Math.min(100, (inCart / deal.qty) * 100);
  const dealName = t(`home.bundles.${deal.id}.title`, {
    defaultValue: `{{qty}} for ${formatPrice(deal.bundlePrice, 'USD', lang)}`,
    qty: deal.qty,
  });

  return (
    <li className="text-xs">
      <div className="flex items-center justify-between gap-3 mb-1">
        <span className="font-body text-[0.65rem] tracking-[0.15em] uppercase text-foreground/75">
          {dealName}
        </span>
        <span
          className={`font-body text-[0.6rem] tracking-[0.2em] uppercase ${
            unlocked ? 'text-accent' : 'text-foreground/55'
          }`}
        >
          {unlocked
            ? t('cart.bundles.unlocked', 'Unlocked ✓')
            : t('cart.bundles.remaining', { defaultValue: '+{{n}} more', n: remaining })}
        </span>
      </div>
      <div className="h-1 w-full bg-foreground/10 overflow-hidden rounded-full">
        <span
          className={`block h-full transition-[width] duration-500 ${
            unlocked ? 'bg-accent' : 'bg-foreground/60'
          }`}
          style={{ width: `${pct}%` }}
        />
      </div>
      {!unlocked && (
        <Link
          to={`/collections/sale#${deal.id}`}
          onClick={onClose}
          className="mt-1 inline-block font-body text-[0.6rem] tracking-[0.25em] uppercase text-foreground/65 hover:text-foreground underline-offset-4 hover:underline"
        >
          {t('cart.bundles.shopMore', 'Add more →')}
        </Link>
      )}
    </li>
  );
}
